import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ExternalLink, Maximize2 } from 'lucide-react';
import { vipAPI } from '../../../services/api';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../ui/dialog';
import { Spinner } from '../../ui/spinner';
import { cn } from '../../../lib/utils';
import { apiError, formatShortDay } from './trackerView';
import { playerLogPath } from './logView';

// usePlayerContacts loads the player's contacts (newest first) while active; reload refetches
// after a save.
export function usePlayerContacts(userId, active) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reload = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const res = await vipAPI.getPlayerContacts(userId, signal);
      setContacts(res.data.contacts || []);
    } catch (err) {
      if (signal?.aborted) return;
      setError(apiError(err, 'Failed to load contact history.'));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!active || !userId) return undefined;
    setContacts([]);
    const controller = new AbortController();
    reload(controller.signal);
    return () => controller.abort();
  }, [active, userId, reload]);

  return { contacts, loading, error, reload };
}

function EmptyHistory({ name }) {
  return (
    <div className="flex min-h-[220px] flex-1 flex-col items-center justify-center gap-2.5 rounded-xl border border-dashed border-white/15 p-5 text-center">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-surface-2 text-ink-dim">
        <Clock className="h-5 w-5" />
      </div>
      <div className="text-[15px] font-bold text-ink">First contact with {name}</div>
      <p className="max-w-[260px] text-[13px] leading-relaxed text-ink-dim">
        Nothing logged yet. Saved contacts show up here, newest first, so the next manager knows what was already offered.
      </p>
    </div>
  );
}

const managerName = (email) => String(email || '').split('@')[0];

function HistoryItem({ entry, large }) {
  return (
    <li className={cn('rounded-lg border border-white/10 bg-surface-1', large ? 'px-5 py-4' : 'px-3.5 py-3')}>
      <div className="flex justify-between gap-2 text-[13px]">
        <Link
          to={playerLogPath(entry.user_id)} title="Open in the contact log"
          className="font-bold text-ink hover:text-cta hover:underline"
        >
          {formatShortDay(entry.contacted_at)} · {entry.channel}
        </Link>
        <span className="truncate text-ink-dim" title={entry.manager_email}>{managerName(entry.manager_email)}</span>
      </div>
      {entry.note && <p className={cn('mt-1.5 whitespace-pre-wrap leading-relaxed text-ink', large ? 'text-[15px]' : 'text-sm')}>{entry.note}</p>}
      <div className="mt-1.5 text-xs text-ink-dim">
        {entry.next_contact_at ? `Next contact ${formatShortDay(entry.next_contact_at)}` : 'No follow-up'}
      </div>
    </li>
  );
}

const countLabel = (n) => (n === 0 ? 'None yet' : `${n} contact${n === 1 ? '' : 's'}`);

// HistoryFullScreen: the whole history in a large window, notes in full.
function HistoryFullScreen({ open, onClose, name, userId, contacts }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex h-[88vh] max-w-4xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
        <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1 border-b border-white/10 px-6 py-4 pr-14">
          <DialogTitle className="text-[17px]">Contact history with {name}</DialogTitle>
          <DialogDescription className="text-[13px]">{countLabel(contacts.length)}, newest first</DialogDescription>
          <Link to={playerLogPath(userId)} className="ml-auto inline-flex items-center gap-1.5 text-[13px] font-bold text-cta hover:underline">
            Open in contact log <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </div>
        <ol className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-6 py-5">
          {contacts.map((c) => <HistoryItem key={c.contact_id} entry={c} large />)}
        </ol>
      </DialogContent>
    </Dialog>
  );
}

// ContactHistory: the player's logged contacts, newest first, with links to the contact log and
// a full-screen view.
export default function ContactHistory({ name, userId, contacts, loading, error, className }) {
  const [full, setFull] = useState(false);
  let body;
  if (loading && contacts.length === 0) body = <div className="flex justify-center py-6"><Spinner /></div>;
  else if (error) body = <p className="text-xs text-negative">{error}</p>;
  else if (contacts.length === 0) body = <EmptyHistory name={name} />;
  else {
    body = (
      <ol className="flex max-h-[380px] min-h-0 flex-col gap-2.5 overflow-y-auto pr-1">
        {contacts.map((c) => <HistoryItem key={c.contact_id} entry={c} />)}
      </ol>
    );
  }
  return (
    <aside aria-label="Contact history" className={cn('relative min-h-0 bg-surface', className)}>
      <div className="flex flex-col gap-3.5 px-6 py-5">
        <div className="flex items-center gap-2 whitespace-nowrap">
          <span className="text-[13px] font-bold uppercase tracking-[0.08em] text-ink-dim">History</span>
          <span className="text-[13px] text-ink-dim">· {countLabel(contacts.length)}</span>
          {contacts.length > 0 && (
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <Link
                to={playerLogPath(userId)} title="Open in the contact log"
                className="rounded-md px-2 py-1.5 text-[13px] font-bold text-cta hover:bg-surface-2 hover:underline"
              >
                Open in log
              </Link>
              <Button
                type="button" variant="ghost" size="icon" className="h-9 w-9"
                aria-label="Show history full screen" title="Full screen" onClick={() => setFull(true)}
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        {body}
      </div>
      <HistoryFullScreen open={full} onClose={() => setFull(false)} name={name} userId={userId} contacts={contacts} />
    </aside>
  );
}
